/**
 * Mock Data Provider for Offline Mode
 *
 * Provides mock API responses for development and testing purposes.
 * Handles routing of API requests to appropriate mock data handlers.
 *
 * @author AssetXP Development Team
 * @version 1.0.0
 */

import sectionMockData from "./data/sections.json";
import attributeMockData from "./data/attributes.json";
import categoryMockData from "./data/categories.json";
import settingsMockData from "./data/settings.json";
import marketsMockData from "./data/markets.json";
import mockUserData from "./data/mockUser.json";
import clonableSectionIdsMockData from "./data/clonableSectionIds.json";
import cloneAttributesDefaultData from "./data/cloneAttributes-default.json";

/**
 * Mock Data Provider Class
 *
 * Simulates backend API responses using static JSON data files.
 * Supports market-specific data, section filtering, and authentication endpoints.
 */
class MockDataProvider {
  /**
   * Initialize the mock data provider with all required data sources
   */
  constructor() {
    this.mockData = {
      sections: sectionMockData,
      attributes: attributeMockData,
      categories: categoryMockData,
      settings: settingsMockData,
      markets: marketsMockData,
      users: mockUserData,
      clonableSectionIds: clonableSectionIdsMockData,
    };
  }

  /**
   * Main entry point for mock API requests
   *
   * @param {string} url - The API endpoint URL
   * @param {string} method - HTTP method (GET, POST, etc.)
   * @param {Object} requestData - Request payload data
   * @returns {Promise<any>} Mock response data
   */
  async getMockData(url, method, requestData) {
    const cleanUrl = this._cleanUrl(url);

    // Handle special authentication and market endpoints
    const authResponse = this._handleAuthEndpoints(cleanUrl);
    if (authResponse) return authResponse;

    // Parse API endpoint structure and query parameters
    const { mainEndpoint, action, id } = this._parseApiEndpoint(cleanUrl);
    const queryParams = this._parseQueryParams(url);
    if (!mainEndpoint) return null;

    console.log(
      `🔌 Mock API: ${method} ${mainEndpoint}/${action}${id ? `/${id}` : ""}`
    );

    return this._routeToHandler(
      mainEndpoint,
      action,
      id,
      method,
      requestData,
      queryParams
    );
  }

  /**
   * Clean and normalize URL for processing
   *
   * @private
   * @param {string} url - Raw URL string
   * @returns {string} Cleaned URL without query params and trailing slashes
   */
  _cleanUrl(url) {
    return url.split("?")[0].replace(/\/$/, "");
  }

  /**
   * Parse query parameters from URL
   *
   * @private
   * @param {string} url - Full URL string with query params
   * @returns {Object} Parsed query parameters
   */
  _parseQueryParams(url) {
    const queryString = url.split("?")[1];
    if (!queryString) return {};

    const params = {};
    queryString.split("&").forEach((param) => {
      const [key, value] = param.split("=");
      if (key && value) {
        params[decodeURIComponent(key)] = decodeURIComponent(value);
      }
    });
    return params;
  }

  /**
   * Handle authentication and market-related endpoints
   *
   * @private
   * @param {string} cleanUrl - Cleaned URL string
   * @returns {Object|null} Auth response data or null if not an auth endpoint
   */
  _handleAuthEndpoints(cleanUrl) {
    // Market endpoints
    if (cleanUrl.includes("/open/GetMarkets")) {
      return [
        { id: "market-1", name: "North America", code: "NA" },
        { id: "market-2", name: "Europe", code: "EU" },
        { id: "market-3", name: "Asia Pacific", code: "APAC" },
      ];
    }

    // Team endpoints
    if (cleanUrl.includes("/open/GetTeams")) {
      return [
        {
          id: "team-1",
          name: "Product Team",
          description: "Product management team",
        },
        {
          id: "team-2",
          name: "Marketing Team",
          description: "Marketing and communications",
        },
        {
          id: "team-3",
          name: "Development Team",
          description: "Software development",
        },
      ];
    }

    // User info endpoints
    if (
      cleanUrl.includes("/userinfo") ||
      cleanUrl.includes("/connect/userinfo")
    ) {
      const defaultUser = this._getDefaultUser();
      return defaultUser.profile;
    }

    // Token endpoints
    if (cleanUrl.includes("/connect/token")) {
      const defaultUser = this._getDefaultUser();
      return {
        access_token: defaultUser.access_token,
        expires_in: defaultUser.expires_in,
        token_type: defaultUser.token_type,
        scope: defaultUser.scope,
      };
    }

    return null;
  }

  /**
   * Get default user from mock data
   *
   * @private
   * @returns {Object} Default user object
   */
  _getDefaultUser() {
    return (
      this.mockData.users.mockUsers.find(
        (user) => user.id === this.mockData.users.defaultUser
      ) || this.mockData.users.mockUsers[0]
    );
  }

  /**
   * Parse API endpoint URL to extract components
   *
   * @private
   * @param {string} cleanUrl - Cleaned URL string
   * @returns {Object} Parsed endpoint components
   */
  _parseApiEndpoint(cleanUrl) {
    const urlParts = cleanUrl.split("/");
    const apiIndex = urlParts.findIndex(
      (part) =>
        part.includes("44304") || part.includes("44331") || part === "api"
    );

    if (apiIndex === -1) {
      return this._handleFallbackParsing(cleanUrl, urlParts);
    }

    const endpointParts = urlParts.slice(apiIndex + 1);
    let mainEndpoint = endpointParts[0];
    let action = endpointParts[1];
    let id = endpointParts[2];

    // Handle explicit 'api' segment: /api/{main}/{action}/{id}
    if (mainEndpoint === "api") {
      mainEndpoint = endpointParts[1];
      action = endpointParts[2];
      id = endpointParts[3];
    }

    return { mainEndpoint, action, id };
  }

  /**
   * Handle URL parsing when standard API index is not found
   *
   * @private
   * @param {string} cleanUrl - Cleaned URL string
   * @param {Array<string>} urlParts - URL split into parts
   * @returns {Object} Parsed endpoint components or empty object
   */
  _handleFallbackParsing(cleanUrl, urlParts) {
    console.warn(
      `Mock data provider: Could not find API index in URL: ${cleanUrl}`
    );

    const supportedEndpoints = [
      "section",
      "asset",
      "attribute",
      "category",
      "axpsettings",
    ];

    if (
      supportedEndpoints.some((endpoint) => cleanUrl.includes(`/${endpoint}/`))
    ) {
      const fallbackIndex = urlParts.findIndex((part) =>
        supportedEndpoints.includes(part)
      );

      if (fallbackIndex !== -1) {
        const endpointParts = urlParts.slice(fallbackIndex);
        console.log(`Using fallback parsing for: ${endpointParts.join("/")}`);

        return {
          mainEndpoint: endpointParts[0],
          action: endpointParts[1],
          id: endpointParts[2],
        };
      }
    }

    return {};
  }

  /**
   * Route requests to appropriate handler based on endpoint
   *
   * @private
   * @param {string} mainEndpoint - Main API endpoint (section, asset, etc.)
   * @param {string} action - Action to perform (get, search, etc.)
   * @param {string} id - Resource identifier
   * @param {string} method - HTTP method
   * @param {Object} requestData - Request payload
   * @param {Object} queryParams - Query parameters from URL
   * @returns {any} Mock response data
   */
  _routeToHandler(mainEndpoint, action, id, method, requestData, queryParams) {
    const handlers = {
      Markets: () => this._handleMarketsEndpoint(action, method),
      markets: () => this._handleMarketsEndpoint(action, method),
      ClonableAssets: () =>
        this._handleClonableAssetsEndpoint(
          action,
          method,
          requestData,
          queryParams
        ),
      section: () =>
        this._handleSectionEndpoint(action, id, method, requestData),
      asset: () => this._handleAssetEndpoint(action, id, method, requestData),
      assetcontext: () =>
        this._handleAssetEndpoint(action, id, method, requestData),
      attribute: () =>
        this._handleAttributeEndpoint(action, id, method, requestData),
      category: () =>
        this._handleCategoryEndpoint(action, id, method, requestData),
      axpsettings: () => this._handleSettingsEndpoint(action, method),
    };

    const handler = handlers[mainEndpoint];
    if (handler) {
      return handler();
    }

    console.warn(`No mock data handler for endpoint: ${mainEndpoint}`);
    return null;
  }

  /**
   * Handle section-related API endpoints
   *
   * @private
   * @param {string} action - Action to perform
   * @param {string} id - Section identifier
   * @param {string} method - HTTP method
   * @param {Object} requestData - Request data
   * @returns {any} Section mock data
   */
  _handleSectionEndpoint(action, id, method, requestData) {
    const actions = {
      getheaders: () => this.mockData.sections.headers,

      get: () => this._findSection(id),
      getbyid: () => this._findSection(id),

      getsectiondata: () => ({
        section: this._findSection(id),
        categories: this.mockData.categories.categories,
        attributes: this.mockData.attributes.attributes,
      }),

      getcategories: () => this.mockData.categories.categories,
      getattributes: () => this.mockData.attributes.attributes,
      getsearchdata: () => this._buildSearchData(id),
    };

    return (
      actions[action]?.() || {
        success: true,
        message: "Mock operation completed",
      }
    );
  }

  /**
   * Find section by ID or name
   *
   * @private
   * @param {string} id - Section identifier
   * @returns {Object} Section object
   */
  _findSection(id) {
    return (
      this.mockData.sections.sections.find(
        (s) => s.id === id || s.name === id
      ) || this.mockData.sections.sections[0]
    );
  }

  /**
   * Build search data response for a section
   *
   * @private
   * @param {string} id - Section identifier
   * @returns {Object} Search data with attributes, categories, and section ID
   */
  _buildSearchData(id) {
    try {
      const mappedAttrs = this._mapAttributes();
      const requestedSection = this._findSection(id);
      const sectionId = requestedSection ? requestedSection.id : "section-1";

      return {
        attributes: mappedAttrs,
        categories: this.mockData.categories.categories,
        sectionid: sectionId,
      };
    } catch (e) {
      console.warn(`Mock getsearchdata mapping error: ${e.message}`);
      return {
        attributes: this.mockData.attributes.attributes,
        categories: this.mockData.categories.categories,
        sectionid: id || "section-1",
      };
    }
  }

  /**
   * Map raw attributes to expected schema format
   *
   * @private
   * @returns {Array<Object>} Mapped attributes array
   */
  _mapAttributes() {
    const rawAttrs = this.mockData.attributes.attributes || [];

    return rawAttrs.map((a) => {
      // Map attribute types: 0: MULTI, 1: STRING, 2: NUMERIC, 3: ASSETARRAY
      const typeMap = { select: 0, number: 2, text: 1 };
      const attributeType = typeMap[a.type] || 1;

      const multiChoiceItems = Array.isArray(a.options)
        ? a.options.map((opt) => ({ id: opt.id, name: opt.label }))
        : [];

      return {
        id: a.id,
        name: a.name,
        attributeType,
        multiChoiceItems,
        catFilters: [],
        sectionFilters: [],
      };
    });
  }

  /**
   * Handle asset-related API endpoints
   *
   * @private
   * @param {string} action - Action to perform
   * @param {string} id - Asset identifier
   * @param {string} method - HTTP method
   * @param {Object} requestData - Request data
   * @returns {any} Asset mock data
   */
  _handleAssetEndpoint(action, id, method, requestData) {
    const actions = {
      search: () => this._searchAssets(requestData),
      searchWithAssetList: () => this._searchAssets(requestData),
      "cross-market-search": () => this._crossMarketSearch(requestData),
      get: () => this._getAsset(id),
      getbasic: () => this._getAsset(id),
      getlisting: () => this.mockData.assets.listings?.[0] || {},
      importassetsfromfile: () => `mock-import-job-${Date.now()}`,
      getassetimportjobstatus: () => ({
        percentComplete: 1,
        statusMessage: "Complete (mock)",
        completitionState: 1,
        dryRun: false,
        reportAvailable: false,
        canCommit: false,
      }),
    };

    return (
      actions[action]?.() || {
        success: true,
        message: "Mock operation completed",
      }
    );
  }

  /**
   * Search assets with section-specific data loading
   *
   * @private
   * @param {Object} requestData - Search parameters
   * @returns {Object} Search results
   */
  _searchAssets(requestData) {
    const sectionId = requestData?.sectionId || requestData?.sectionid;
    let assets = [];

    if (sectionId) {
      // Try to load section-specific assets using new file structure
      const sectionAssets = this._loadSearchAssets("generic", sectionId);
      if (sectionAssets) {
        assets = sectionAssets.results || sectionAssets;
      } else {
        // Fallback to empty array
        assets = [];
      }
    } else {
      // No section specified, return generic assets from both sections
      const posAssets = this._loadSearchAssets("generic", "pos");
      const activationAssets = this._loadSearchAssets("generic", "activations");
      assets = [
        ...(posAssets?.results || []),
        ...(activationAssets?.results || []),
      ];
    }

    return {
      results: assets,
      hasNextPage: false,
      token: "mock-token",
    };
  }

  /**
   * Perform cross-market asset search with section filtering
   *
   * @private
   * @param {Object} requestData - Search parameters including marketId and sectionId
   * @returns {Object} Market-specific search results
   */
  _crossMarketSearch(requestData) {
    const { marketId, sectionId = requestData?.sectionid } = requestData || {};
    let assets = [];

    if (sectionId && marketId) {
      // Map marketId to region name
      const region = this._mapMarketToRegion(marketId);

      // Try to get market-section-specific assets using new file structure
      const marketSectionAssets = this._loadSearchAssets(region, sectionId);
      if (marketSectionAssets) {
        assets = marketSectionAssets.results || marketSectionAssets;
      } else {
        // Fallback to generic section assets
        const sectionAssets = this._loadSearchAssets("generic", sectionId);
        if (sectionAssets) {
          assets = sectionAssets.results || sectionAssets;
        } else {
          // Final fallback to market assets
          assets = this._getMarketAssets(marketId);
        }
      }
    } else if (marketId) {
      // No section specified, return market assets
      assets = this._getMarketAssets(marketId);
    } else if (sectionId) {
      // No market specified, return generic section assets
      const sectionAssets = this._loadSearchAssets("generic", sectionId);
      if (sectionAssets) {
        assets = sectionAssets.results || sectionAssets;
      }
    }

    console.log(
      `🔌 Cross-market search: ${marketId}/${sectionId} → ${
        assets.length
      } assets`
    );

    return {
      results: assets,
      hasNextPage: false,
      token: `mock-token-market-${marketId}`,
    };
  }

  /**
   * Get assets for a specific market (now handled by search files)
   *
   * @private
   * @param {string} marketId - Market identifier
   * @returns {Array<Object>} Market-specific assets
   */
  _getMarketAssets(marketId) {
    // Market assets are now handled by the search files
    // Return empty array as fallback - assets should be loaded via _loadSearchAssets
    console.warn(
      `_getMarketAssets called for market: ${marketId}. Use _loadSearchAssets instead.`
    );
    return [];
  }

  /**
   * Load search assets using new file structure
   *
   * @private
   * @param {string} region - Region identifier ("generic", "us", "eu")
   * @param {string} sectionId - Section identifier (GUID)
   * @returns {Object|null} Search assets or null if not found
   */
  _loadSearchAssets(region, sectionId) {
    // Map section ID to section name
    const sectionName = this._mapSectionIdToSectionName(sectionId);

    if (sectionName) {
      try {
        const searchAssets = require(`./data/assets/search-${region}-${sectionName}.json`);
        return searchAssets;
      } catch (e) {
        console.warn(
          `Search assets file not found: search-${region}-${sectionName}.json`
        );
      }
    }

    return null;
  }

  /**
   * Map market ID to region name
   *
   * @private
   * @param {string} marketId - Market identifier
   * @returns {string} Region name
   */
  _mapMarketToRegion(marketId) {
    // Market IDs are now directly us/eu, no mapping needed
    if (marketId === "us" || marketId === "eu") {
      return marketId;
    }
    return "generic";
  }

  /**
   * Map section ID to section name for new file structure
   *
   * @private
   * @param {string} sectionId - Section identifier (GUID)
   * @returns {string|null} Section name or null if not found
   */
  _mapSectionIdToSectionName(sectionId) {
    // Section IDs are now directly pos/activations, no complex mapping needed
    if (sectionId === "pos" || sectionId === "activations") {
      return sectionId;
    }

    // Find section by ID in the sections data for backward compatibility
    const section = this.mockData.sections.sections.find(
      (s) => s.id === sectionId
    );
    if (section) {
      // Map section names to file suffixes
      const nameMap = {
        POS: "pos",
        Activations: "activations",
      };
      return nameMap[section.name] || null;
    }

    return null;
  }

  /**
   * Filter assets by section ID
   *
   * @private
   * @param {Array<Object>} assets - Assets array to filter
   * @param {string} sectionId - Section identifier
   * @returns {Array<Object>} Filtered assets
   */
  _filterAssetsBySection(assets, sectionId) {
    return assets.filter(
      (asset) => asset.sectionId === sectionId || asset.sectionid === sectionId
    );
  }

  /**
   * Get a specific asset by ID using new file structure
   *
   * @private
   * @param {string} id - Asset identifier
   * @returns {Object} Asset object or fallback asset
   */
  _getAsset(id) {
    // Try to load individual asset file using new naming convention
    const possibleFiles = this._generateAssetFilenames(id);

    for (const filename of possibleFiles) {
      try {
        const assetData = require(`./data/assets/${filename}`);
        return assetData;
      } catch (e) {
        // File not found, try next
      }
    }

    // Search in all search files as fallback
    const searchFiles = [
      "search-generic-pos.json",
      "search-generic-activations.json",
      "search-us-pos.json",
      "search-us-activations.json",
      "search-eu-pos.json",
      "search-eu-activations.json",
    ];

    for (const searchFile of searchFiles) {
      try {
        const searchData = require(`./data/assets/${searchFile}`);
        const asset = searchData.results?.find((a) => a.id === id);
        if (asset) {
          // Try to load detailed asset data using new naming
          for (const filename of possibleFiles) {
            try {
              const detailedAsset = require(`./data/assets/${filename}`);
              return detailedAsset;
            } catch (e) {
              // File not found, try next
            }
          }
          // Return basic asset info if detailed file not found
          return asset;
        }
      } catch (e) {
        // Search file not found, continue
      }
    }

    console.warn(`Mock asset not found: ${id}`);
    return null;
  }

  /**
   * Generate possible asset filenames based on asset ID
   *
   * @private
   * @param {string} id - Asset identifier
   * @returns {Array<string>} Possible filenames
   */
  _generateAssetFilenames(id) {
    // Map asset IDs to new filenames using consistent naming
    const filenameMap = {
      "generic-pos-01": "assets-generic-pos-01.json",
      "generic-pos-02": "assets-generic-pos-02.json",
      "generic-activation-01": "assets-generic-activation-01.json",
      "generic-activation-02": "assets-generic-activation-02.json",
      "us-pos-01": "assets-us-pos-01.json",
      "us-pos-02": "assets-us-pos-02.json",
      "us-activation-01": "assets-us-activation-01.json",
      "us-activation-02": "assets-us-activation-02.json",
      "eu-pos-01": "assets-eu-pos-01.json",
      "eu-pos-02": "assets-eu-pos-02.json",
      "eu-activation-01": "assets-eu-activation-01.json",
      "eu-activation-02": "assets-eu-activation-02.json",
    };

    const mappedFilename = filenameMap[id];
    if (mappedFilename) {
      return [mappedFilename];
    }

    // Fallback: try direct filename
    return [`assets-${id}.json`];
  }

  /**
   * Handle attribute-related API endpoints
   *
   * @private
   * @param {string} action - Action to perform
   * @param {string} id - Attribute identifier
   * @param {string} method - HTTP method
   * @param {Object} requestData - Request data
   * @returns {any} Attribute mock data
   */
  _handleAttributeEndpoint(action, id, method, requestData) {
    const actions = {
      getall: () => this.mockData.attributes.attributes,
      searchdropdown: () => this._getAttributeDropdownOptions(requestData),
      getAttributeManagement: () => ({
        attributes: this.mockData.attributes.attributes,
        bindingGroups: [],
      }),
    };

    return (
      actions[action]?.() || {
        success: true,
        message: "Mock operation completed",
      }
    );
  }

  /**
   * Get dropdown options for a specific attribute
   *
   * @private
   * @param {Object} requestData - Request data containing attributeid
   * @returns {Array<Object>} Attribute options
   */
  _getAttributeDropdownOptions(requestData) {
    try {
      const attrId = requestData?.attributeid;
      if (attrId) {
        const attr = this.mockData.attributes.attributes.find(
          (a) => a.id === attrId
        );
        if (attr?.options) {
          return attr.options.map((opt) => ({ id: opt.id, name: opt.label }));
        }
      }
    } catch (e) {
      console.warn(`Mock searchdropdown error: ${e.message}`);
    }

    // Fallback options
    return [
      { id: "opt-1", name: "Option 1" },
      { id: "opt-2", name: "Option 2" },
      { id: "opt-3", name: "Option 3" },
    ];
  }

  /**
   * Handle category-related API endpoints
   *
   * @private
   * @param {string} action - Action to perform
   * @param {string} id - Category identifier
   * @param {string} method - HTTP method
   * @param {Object} requestData - Request data
   * @returns {any} Category mock data
   */
  _handleCategoryEndpoint(action, id, method, requestData) {
    const actions = {
      getall: () => this.mockData.categories.categories,
      getallinsection: () => this.mockData.categories.categories,
      getheadersbysection: () => this.mockData.categories.headers,
    };

    return (
      actions[action]?.() || {
        success: true,
        message: "Mock operation completed",
      }
    );
  }

  /**
   * Handle market-related API endpoints
   *
   * @private
   * @param {string} action - Action to perform
   * @param {string} method - HTTP method
   * @returns {Array<Object>} Markets data
   */
  _handleMarketsEndpoint(action, method) {
    if (action === "mymarkets") {
      if (this.mockData?.markets) {
        return Array.isArray(this.mockData.markets)
          ? this.mockData.markets
          : this.mockData.markets.markets || [];
      }
    }
    if (action === "myaccessiblemarkets") {
      return null;
    }
    return [];
  }

  /**
   * Handle ClonableAssets API endpoints
   *
   * @private
   * @param {string} action - Action to perform
   * @param {string} method - HTTP method
   * @param {Object} requestData - Request data for POST operations
   * @param {Object} queryParams - Query parameters from URL
   * @returns {Array<string>|string|Array<Object>} Clonable section IDs, cloned asset ID, or attributes
   */
  _handleClonableAssetsEndpoint(action, method, requestData, queryParams) {
    if (action === "get-clonable-section-ids") {
      return this.mockData.clonableSectionIds || [];
    }

    if (
      action === "get-attributes-without-market-id" &&
      method?.toLowerCase() === "post"
    ) {
      return this._getCloneAttributes(requestData);
    }

    if (action === "clone-to-market" && method?.toLowerCase() === "post") {
      // Log the clone request data for debugging
      console.log("📋 Mock Clone Request Data:", {
        sourceAssetId: requestData?.sourceAssetId,
        targetMarketId: requestData?.targetMarketId,
        userId: requestData?.userId,
        selectedAttributeIds: requestData?.selectedAttributeIds,
        clonedAssetName: requestData?.clonedAssetName,
        clonedAssetDescription: requestData?.clonedAssetDescription,
        clonedAssetId: requestData?.clonedAssetId,
      });

      // Use the provided clonedAssetId if available, otherwise generate one
      const clonedAssetId = requestData?.clonedAssetId || 
        `cloned-${Date.now()}-${Math.random()
          .toString(36)
          .substring(2, 11)}`;

      console.log(`✅ Mock Clone Success: ${clonedAssetId}`);

      return clonedAssetId;
    }

    return [];
  }

  /**
   * Get clone attributes for a specific asset and section
   *
   * @private
   * @param {Object} requestData - Request body containing assetId and sectionId
   * @returns {Array<Object>} Array of clone attributes
   */
  _getCloneAttributes(requestData) {
    const { assetId, sectionId } = requestData || {};

    console.log(
      `🔌 Clone Attributes: ${assetId}/${sectionId} → ${
        cloneAttributesDefaultData.length
      } attributes`
    );
    return cloneAttributesDefaultData;
  }

  /**
   * Extract asset number from asset ID for file mapping
   *
   * @private
   * @param {string} assetId - Asset identifier
   * @returns {string} Asset number (e.g., "01", "02")
   */
  _extractAssetNumber(assetId) {
    // Extract number from asset IDs like "generic-pos-01" or "us-pos-02"
    const match = assetId.match(/-(\d+)$/);
    return match ? match[1].padStart(2, "0") : "01";
  }

  /**
   * Handle settings-related API endpoints
   *
   * @private
   * @param {string} action - Action to perform
   * @param {string} method - HTTP method
   * @returns {any} Settings mock data
   */
  _handleSettingsEndpoint(action, method) {
    const actions = {
      get: () => this.mockData.settings.axpSettings,
      GetAutoCropAllowance: () => this.mockData.settings.autoCropAllowed,
    };

    return actions[action]?.() || { success: true };
  }
}

// Export singleton instance
export default new MockDataProvider();
